import type { UseFormReturn } from 'react-hook-form';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, Form, theme } from '@sg/garnish';

import { type AddressForm } from '../../address.config';

export const AddressFormDeliveryNotes = (props: AddressFormDeliveryNoteProps) => {
  const { form, notes, maxLength } = props;

  const { formatMessage } = useIntl();

  return (
    <View style={styles.deliveryNotes}>
      <View style={styles.header}>
        <BodyText style={styles.text} sizeMatch={['12']}>
          {formatMessage(messages.deliveryNoteLabel)}
        </BodyText>

        <BodyText
          sizeMatch={['14', '16']}
          style={[notes?.length === maxLength && styles.cautionText, styles.text, styles.smallText]}
        >
          {notes?.length}/{maxLength}
        </BodyText>
      </View>

      <Form.TextField
        name="notes"
        control={form.control}
        testID="account.address-form.delivery-notes"
        style={styles.inputField}
        maxLength={maxLength}
        placeholder={formatMessage(messages.deliveryNotePlaceholder)}
        accessibilityLabel={formatMessage(messages.deliveryNoteA11y, {
          max_length: maxLength,
        })}
        multiline
        blurOnSubmit
        returnKeyType="done"
      />
    </View>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly deliveryNoteLabel: NoMessageValues;
  readonly deliveryNotePlaceholder: NoMessageValues;
  readonly deliveryNoteA11y: { readonly max_length: MessageValue };
}>({
  deliveryNoteLabel: {
    defaultMessage: 'Delivery Notes',
    description: 'Address Form | Delivery Notes | Label',
  },
  deliveryNotePlaceholder: {
    defaultMessage: 'Security code, building details, etc.',
    description: 'Address Form | Delivery Notes | Placeholder',
  },
  deliveryNoteA11y: {
    defaultMessage: 'Delivery notes for courier, max of {max_length} characters...',
    description: 'Address Form | Delivery Notes | A11y label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing['1'],
  },
  inputField: { height: 80 },
  text: {
    paddingBottom: theme.spacing['2'],
  },
  deliveryNotes: { paddingTop: theme.spacing['8'] },
  smallText: { fontSize: 10 },
  cautionText: { color: theme.colors.CAUTION },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type AddressFormDeliveryNoteProps = {
  form: UseFormReturn<AddressForm>;
  maxLength: number;
  notes?: string;
};
